"use client";

import { Button } from "./ui/button";

import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ArrowRight, MoreVertical } from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import { useAvailableBalances } from "../hooks/query/queries";
import { useNavigate } from "react-router-dom";
import { currencies } from "../data/mockTradeRates";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useFeatureAccess } from "./FeatureGate";
import { formatFiatWithTwoDecimals } from "../utils/numberFormatter";
import { getAssetNavigationPath, isZar8Asset } from "../utils/assetNavigation";

export default function ForexAssets({ userId, onTransact }) {
  const { flags: featureFlags } = useFeatureAccess("mainFeatures", [
    "buyForex",
    "sellForex",
    "withdrawForex",
    "depositForex",
  ]);
  const { isLoading } = useAvailableBalances(userId);

  const zarAsset = currencies?.find((asset) => asset.asset === "zar");

  const otherAssets = currencies
    ?.filter((asset) => asset.asset !== "zar")
    ?.sort((a, b) => {
      const priceA =
        parseFloat(a.valueInLocalCurrency.replace(/R|,|\s/g, "")) || 0;
      const priceB =
        parseFloat(b.valueInLocalCurrency.replace(/R|,|\s/g, "")) || 0;
      return priceB - priceA;
    });

  const sortedAssets = zarAsset ? [zarAsset, ...otherAssets] : otherAssets;

  const handleTransaction = (asset, type) => {
    if (onTransact) {
      onTransact(asset, type);
    }
  };
  const navigate = useNavigate();
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>Your forex</CardTitle>
      </CardHeader>

      <CardContent className="p-0 flex flex-col flex-1 ">
        <div className="flex-1 px-4 pb-4 space-y-3">
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-4 gap-5 px-6 py-5 items-center rounded-lg bg-white animate-pulse"
                >
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full bg-gray-300" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-20 bg-gray-300" />
                      <Skeleton className="h-3 w-16 bg-gray-300" />
                    </div>
                  </div>

                  <div className="flex flex-col items-start space-y-2">
                    <Skeleton className="h-4 w-24 bg-gray-300" />
                    <Skeleton className="h-3 w-20 bg-gray-300" />
                  </div>

                  <div className="flex flex-col items-start space-y-2">
                    <Skeleton className="h-4 w-28 bg-gray-300" />
                  </div>

                  <div className="flex justify-end items-center gap-4">
                    <Skeleton className="h-10 w-10 rounded-full bg-gray-300" />
                    <Skeleton className="h-10 w-10 rounded-full bg-gray-300" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            sortedAssets?.map((asset) => (
              <div
                key={asset.assetId}
                className="grid grid-cols-4 gap-5 px-6 py-5 items-center hover:bg-gray-100 z-[10] rounded-lg"
                onClick={() =>
                  navigate(getAssetNavigationPath(asset), {
                    state: isZar8Asset(asset) ? null : asset,
                  })
                }
              >
                <div className="flex items-center gap-3">
                  <img
                    src={asset.assetIcon}
                    alt={`${asset.assetName} icon`}
                    className="w-10 h-10"
                  />
                  <div>
                    <div className="font-semibold">
                      {asset.asset.toUpperCase()}
                    </div>
                    <div className="text-gray-500 text-sm">
                      {asset.assetName}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-start">
                  <div className="font-semibold">
                    {asset?.availableAmountFormatted}
                  </div>
                  <div className="text-emerald-500">
                    {asset.asset === "zar"
                      ? asset.availableAmountFormatted
                      : asset.valueInLocalCurrency
                        ? formatFiatWithTwoDecimals(asset.valueInLocalCurrency)
                        : "R 0.00"}
                  </div>
                </div>

                <div className="flex flex-col items-start">
                  <div className="font-semibold">
                    {asset.asset === "zar"
                      ? " "
                      : asset?.assetEstimatedPrice
                          ?.toLocaleString("en-ZA", {
                            minimumFractionDigits: 5,
                            maximumFractionDigits: 5,
                          })
                          .replace(/,/g, " ") || 0}
                  </div>
                  <div className="text-emerald-500">{}</div>
                </div>

                {asset?.asset !== "zar" && (
                  <div className="flex justify-end gap-4 items-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="rounded-full z-[11]"
                        >
                          <MoreVertical className="w-5 h-5 text-gray-500" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        sideOffset={8}
                        className="min-w-[11rem] rounded-lg p-3"
                      >
                        {featureFlags?.buyForex && (
                          <DropdownMenuItem asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTransaction(asset, "a");
                              }}
                              className="flex justify-between items-center w-full gap-14 px-3 py-5 text-sm hover:bg-gray-100"
                            >
                              <span>Buy</span>
                              <div className="flex justify-center items-center border bg-white  h-10 w-10 rounded-full hover:bg-gray-100">
                                <ArrowRight className="w-5 h-5 text-gray-500" />
                              </div>
                            </button>
                          </DropdownMenuItem>
                        )}
                        {featureFlags?.sellForex && (
                          <DropdownMenuItem asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTransaction(asset, "b");
                              }}
                              className="flex justify-between items-center w-full gap-14 px-3 py-5 text-sm hover:bg-gray-100"
                            >
                              <span>Sell</span>
                              <div className="flex justify-center items-center border bg-white  h-10 w-10 rounded-full hover:bg-gray-100">
                                <ArrowRight className="w-5 h-5 text-gray-500" />
                              </div>
                            </button>
                          </DropdownMenuItem>
                        )}
                        {featureFlags?.withdrawForex && (
                          <DropdownMenuItem asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTransaction(asset, "c");
                              }}
                              className="flex justify-between items-center w-full gap-14 px-3 py-5 text-sm hover:bg-gray-100"
                            >
                              <span>Send</span>
                              <div className="flex justify-center items-center border bg-white  h-10 w-10 rounded-full hover:bg-gray-100">
                                <ArrowRight className="w-5 h-5 text-gray-500" />
                              </div>
                            </button>
                          </DropdownMenuItem>
                        )}
                        {featureFlags?.depositForex && (
                          <DropdownMenuItem asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTransaction(asset, "d");
                              }}
                              className="flex justify-between items-center w-full gap-14 px-3 py-5 text-sm hover:bg-gray-100"
                            >
                              <span>Receive</span>
                              <div className="flex justify-center items-center border bg-white  h-10 w-10 rounded-full hover:bg-gray-100">
                                <ArrowRight className="w-5 h-5 text-gray-500" />
                              </div>
                            </button>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <div
                      className="flex justify-center items-center border bg-white h-10 w-10 rounded-full hover:bg-gray-100"
                      onClick={() =>
                        navigate(getAssetNavigationPath(asset), {
                          state: isZar8Asset(asset) ? null : asset,
                        })
                      }
                    >
                      <ArrowRight className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
